import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useForm } from "@mantine/form";
import { Button, TextInput, PasswordInput } from "@mantine/core";
import { ReactComponent as GoogleIcon } from "@/assets/icons/google.svg";
import { ReactComponent as Logo } from "@/assets/dinamo-logo.svg";
import { errorNotification } from "@/helpers/notifications";
import { useMe } from "@/context/MeContext";
import { AuthApi } from "@/api/auth.api";
import "./Login.css";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { fetchMe } = useMe();

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value: string) =>
        !value
          ? "E-mail je obavezan"
          : /^\S+@\S+$/.test(value)
          ? null
          : "E-mail nije ispravan",
      password: (value: string) => (!value ? "Lozinka je obavezna" : null),
    },
  });

  const loginHandler = async (values: typeof form.values) => {
    const response = await AuthApi.login(values);
    if (!response.payload) {
      errorNotification({
        message: response.response.data.message,
      });
    } else await fetchMe();
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    scope: "openid email profile",
    onSuccess: async (response) => {
      try {
        const res = await AuthApi.googleLogin(response.access_token);
        if (!res) throw new Error();
        if (res && !res.payload) {
          errorNotification({
            message: res.message,
          });
        } else await fetchMe();
      } catch (error) {
        console.error("Google login failed", error);
        errorNotification({
          message: "Google prijava nije uspjela",
        });
      }
    },
  });

  return (
    <div className="login-container">
      <form className="login-card" onSubmit={form.onSubmit(loginHandler)}>
        <div className="mobile-logo">
          <Logo className="w-20 h-20" />
        </div>
        <div className="text-center text-[#0b1a29] text-xl font-bold">
          Prijava
        </div>
        <Button
          variant="outline"
          color="#0b1a29"
          radius="xl"
          size="md"
          className="!border-[#e6e6e6] !h-[3.25rem]"
          leftSection={<GoogleIcon />}
          onClick={() => googleLogin()}
        >
          Prijava s Google računom
        </Button>
        <div className="justify-center items-center gap-6 inline-flex">
          <div className="w-20 h-px bg-[#cccccc] rounded-sm" />
          <div className="text-center text-[#cccccc] text-sm font-medium ">
            ili
          </div>
          <div className="w-20 h-px bg-[#cccccc] rounded-sm" />
        </div>
        <div>
          <div className="flex flex-col gap-6">
            <TextInput
              label="E-mail"
              placeholder="E-mail adresa"
              key={form.key("email")}
              {...form.getInputProps("email")}
            />
            <PasswordInput
              label="Lozinka"
              placeholder="Lozinka"
              key={form.key("password")}
              {...form.getInputProps("password")}
            />
          </div>
          <div className="flex justify-start p-0">
            <Button
              variant="transparent"
              color="#004a99"
              className="!px-0 hover:opacity-80"
              classNames={{
                label: "text-[#004a99] text-sm font-medium underline",
              }}
              onClick={() => navigate("/forgotten-password")}
            >
              Zaboravljena lozinka?
            </Button>
          </div>
        </div>
        <Button
          type="submit"
          variant="filled"
          color="#004a99"
          radius="xl"
          size="md"
          className="!h-[3.25rem]"
        >
          Prijava
        </Button>
        <div className="text-center">
          <span className="text-[#0b1a29] text-sm font-medium ">
            Nemaš račun?
          </span>
          <Button
            variant="transparent"
            color="#004a99"
            className="!px-1 hover:opacity-80"
            classNames={{
              label: "text-[#004a99] text-sm font-bold underline",
            }}
            onClick={() => navigate("/registration")}
          >
            Registriraj se
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Login;
